import { EditOutlined, LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Divider, Flex } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MenuProps, Table } from 'antd/lib'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useClassifierServiceDeleteResultValueOptionEndpoint,
  useClassifierServiceGetListResultValueOptionsEndpoint,
  useClassifierServiceGetListResultValueOptionsEndpointKey
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import {
  DeleteConfirm,
  message,
  PageBreadcrumbs,
  TableActionButtons
} from 'src/components'
import { useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { ValueOptionModal } from '../components'

export const ValuesOptionsPage = () => {
  const { t, currentLang } = useLocales()
  const navigate = useNavigate()

  const pagination = useTablePagination()
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)

  const [currentId, setCurrentId] = useState<string>()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize
  }

  const { data, isLoading } =
    useClassifierServiceGetListResultValueOptionsEndpoint(queryParams)

  const { mutate } = useClassifierServiceDeleteResultValueOptionEndpoint({
    onError: (err: ApiError) => formErrorHandler(err.body.errors),
    onSuccess: () => {
      message.success(t('laboratory_classifiers.delete_success_option'))
      queryClient.invalidateQueries({
        queryKey: [useClassifierServiceGetListResultValueOptionsEndpointKey]
      })
    }
  })

  const onToggle = () => setIsOpen((prev) => !prev)

  const onCloseModal = () => {
    onToggle()
    setCurrentId(undefined)
  }

  const onBackClick = () => navigate(-1)

  const getItems = (id: string): MenuProps['items'] => [
    {
      key: 'edit',
      label: t('common.edit'),
      icon: <EditOutlined />,
      onClick: () => {
        setCurrentId(id)
        onToggle()
      }
    },
    {
      key: 'delete',
      danger: true,
      label: (
        <DeleteConfirm
          title={t('laboratory_classifiers.delete_option')}
          onConfirm={() =>
            mutate({
              id: String(id)
            })
          }
        />
      )
    }
  ]

  const columns: ColumnsType = [
    {
      key: 'name',
      title: t('medicine_register.products.name'),
      render: ({ translation }) => translation?.[currentLang?.value]
    },

    {
      key: 'comments',
      title: t('common.comments'),
      render: (_, { comments }) => comments || '-'
    },
    {
      key: 'order',
      title: t('common.order'),
      render: (_, { order }) => order ?? '-'
    },
    {
      key: 'actions',
      render: ({ id }) => (
        <TableActionButtons justify="flex-end" items={getItems(id)} />
      )
    }
  ]

  return (
    <PageBreadcrumbs
      title={t('laboratory_classifiers.value_options')}
      items={[
        { title: t('menu.laboratory_classifiers') },
        { title: t('laboratory_classifiers.value_options') }
      ]}
    >
      <Flex vertical>
        <Flex justify="space-between">
          <Button icon={<LeftOutlined />} onClick={onBackClick}>
            {t('common.back')}
          </Button>

          <Button type="primary" onClick={onToggle}>
            {t('laboratory_classifiers.add_option')}
          </Button>
        </Flex>

        <Divider />

        <Table
          rowKey="id"
          columns={columns}
          loading={isLoading}
          dataSource={data?.data}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>

      {isOpen && <ValueOptionModal id={currentId} onCancel={onCloseModal} />}
    </PageBreadcrumbs>
  )
}
